/**
 * RealtimeService — Live WebSocket sync.
 *
 * Connects to the backend WebSocket scoped by sync_key (workspace_id OR
 * personal user_id). When a `data_updated` event arrives, we trigger a
 * cloud pull via AutoSync (which uses suppressSync mode so the resulting
 * localStorage writes don't loop back into a push), then fire a
 * `maidly:cloud-data-updated` window event so individual pages can re-read
 * their slice without needing realtime knowledge themselves.
 *
 * Reconnect strategy: exponential backoff capped at 30s. Browser-online
 * event triggers an immediate reconnect attempt.
 */
import syncService from './SyncService';
import autoSync from './AutoSync';
import tokenStore from './tokenStore';

const MAX_BACKOFF_MS = 30_000;
const PING_INTERVAL_MS = 25_000;

class RealtimeService {
  constructor() {
    this.ws = null;
    this.syncKey = null;
    this.token = null;
    this.deviceId = null;
    this.backoffMs = 1000;
    this.reconnectTimer = null;
    this.pingTimer = null;
    this.shouldReconnect = false;
    this.pullDebounce = null;
    this.pullPending = false;
    // Presence cache: { userId: { status, name, role, last_seen, device_ids } }
    this.presence = {};
    this.presenceSubscribers = new Set();

    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        if (this.shouldReconnect && !this.isConnected()) this.reconnectNow();
      });
    }
  }

  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN;
  }

  /** Open a connection. Idempotent — closes prior socket if any. */
  connect(syncKey, token, deviceId) {
    if (!syncKey) return;
    // Tear down any prior connection (e.g. user switched workspaces)
    this.disconnect();
    this.syncKey = syncKey;
    this.token = token || null;
    this.deviceId = deviceId || null;
    this.shouldReconnect = true;
    this.backoffMs = 1000;
    this._open();
  }

  _open() {
    const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
    if (!apiUrl) return;
    const wsUrl = apiUrl.replace(/^http/i, 'ws');
    const qs = new URLSearchParams();
    if (this.token) qs.set('token', this.token);
    if (this.deviceId) qs.set('device_id', this.deviceId);
    const url = `${wsUrl}/api/ws/${encodeURIComponent(this.syncKey)}?${qs.toString()}`;

    try {
      this.ws = new WebSocket(url);
    } catch (e) {
      this._scheduleReconnect();
      return;
    }

    this.ws.onopen = () => {
      this.backoffMs = 1000;
      this._startPing();
    };

    this.ws.onmessage = (evt) => {
      let msg = null;
      try {
        msg = JSON.parse(evt.data);
      } catch {
        return;
      }
      if (!msg || typeof msg !== 'object') return;
      if (msg.type === 'data_updated') {
        this._handleDataUpdated(msg);
      } else if (msg.type === 'presence') {
        this._handlePresence(msg);
      }
      // 'pong' and 'connected' are no-ops here
    };

    this.ws.onclose = () => {
      this._stopPing();
      if (this.shouldReconnect) this._scheduleReconnect();
    };

    this.ws.onerror = () => {
      // Let onclose handle reconnect logic
    };
  }

  _handleDataUpdated(msg) {
    // Fast path: if the event carries `records` and/or `deleted_ids`, apply
    // them directly to localStorage without a full cloud pull. AutoSync
    // suppresses re-publish so the patch doesn't loop back.
    const hasDelta =
      (msg.records && typeof msg.records === 'object') ||
      (msg.deleted_ids && typeof msg.deleted_ids === 'object');

    if (hasDelta) {
      try {
        this._applyDelta(msg);
        window.dispatchEvent(
          new CustomEvent('maidly:cloud-data-updated', { detail: msg })
        );
        return;
      } catch (e) {
        // Delta apply failed (likely a stale local snapshot) — fall through to
        // a full pull which is slower but always correct.
        console.warn('[Realtime] delta apply failed; falling back to bulk pull', e);
      }
    }

    // Slow path / fallback: pull bulk data. Debounced so bursts coalesce.
    if (this.pullDebounce) clearTimeout(this.pullDebounce);
    this.pullDebounce = setTimeout(async () => {
      this.pullDebounce = null;
      if (this.pullPending) return;
      this.pullPending = true;
      try {
        await autoSync.pullFromCloud();
        window.dispatchEvent(
          new CustomEvent('maidly:cloud-data-updated', { detail: msg })
        );
      } catch (e) {
        // Pull failed (network blip, 401 while refresh in flight, etc.) — the
        // next inbound delta event will retry this same code path.
        console.warn('[Realtime] bulk pull failed; will retry on next event', e);
      } finally {
        this.pullPending = false;
      }
    }, 250);
  }

  /**
   * Merge a delta payload directly into localStorage.
   * Uses AutoSync's suppressSync flag so the resulting localStorage writes
   * don't re-trigger an outbound cloud push.
   */
  _applyDelta(msg) {
    const SYNCED_KEYS = {
      customers: 'maidly_customers',
      invoices: 'maidly_invoices',
      quotes: 'maidly_quotes',
      appointments: 'maidly_appointments',
      timeEntries: 'maidly_timeEntries',
      employees: 'maidly_employees',
      mileageEntries: 'maidly_mileageEntries',
      expenses: 'maidly_expenses',
      settings: 'maidly_settings',
    };

    const records = msg.records || {};
    const deletes = msg.deleted_ids || {};
    const touchedTypes = new Set([
      ...Object.keys(records),
      ...Object.keys(deletes),
    ]);
    if (touchedTypes.size === 0) return;

    autoSync.suppressSync = true;
    try {
      touchedTypes.forEach((dataType) => {
        const key = SYNCED_KEYS[dataType];
        if (!key) return; // unknown type — skip silently

        let arr = [];
        try {
          arr = JSON.parse(localStorage.getItem(key) || '[]');
          if (!Array.isArray(arr)) arr = [];
        } catch {
          arr = [];
        }

        const byId = new Map(arr.filter((x) => x && x.id).map((x) => [x.id, x]));

        // Upserts
        (records[dataType] || []).forEach((rec) => {
          if (rec && rec.id) byId.set(rec.id, rec);
        });
        // Deletions
        (deletes[dataType] || []).forEach((id) => byId.delete(id));

        const next = Array.from(byId.values());
        localStorage.setItem(key, JSON.stringify(next));
        // Keep AutoSync's diff snapshot in sync so the next local edit
        // computes the right field-diff against the cloud's current view.
        try {
          const snapMap = new Map();
          next.forEach((it) => {
            if (it && it.id) snapMap.set(it.id, it);
          });
          autoSync.lastSnapshot.set(dataType, snapMap);
        } catch (e) {
          // Snapshot update is best-effort — a stale snapshot just means the
          // next local edit will compute a slightly noisier field-diff.
          console.warn('[Realtime] snapshot update failed', e);
        }
      });

      // App settings (single OBJECT record, not a list) — apply live with
      // last-write-wins so company info/logo updates propagate across devices
      // immediately, not just on the next reload.
      const settingsRec = Array.isArray(records.app_settings) ? records.app_settings[0] : null;
      if (settingsRec) {
        let local = {};
        try { local = JSON.parse(localStorage.getItem('maidly_settings') || '{}'); } catch { local = {}; }
        const cloudTs = Date.parse(settingsRec._last_updated_at || 0) || 0;
        const localTs = Date.parse(local._last_updated_at || 0) || 0;
        if (cloudTs >= localTs) {
          const { id, ...settingsObj } = settingsRec;
          localStorage.setItem('maidly_settings', JSON.stringify(settingsObj));
          window.dispatchEvent(new CustomEvent('maidly:settings-updated'));
        }
      }
    } finally {
      autoSync.suppressSync = false;
    }
  }

  _scheduleReconnect() {
    if (this.reconnectTimer) return;
    const delay = this.backoffMs;
    this.backoffMs = Math.min(this.backoffMs * 2, MAX_BACKOFF_MS);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.shouldReconnect) this._open();
    }, delay);
  }

  reconnectNow() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.shouldReconnect) this._open();
  }

  _startPing() {
    this._stopPing();
    this.pingTimer = setInterval(() => {
      if (this.isConnected()) {
        try {
          this.ws.send('ping');
        } catch (e) {
          // Socket transitioning to closed — the reconnect timer handles it.
          console.warn('[Realtime] ping send failed', e);
        }
      }
    }, PING_INTERVAL_MS);
  }

  _stopPing() {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  disconnect() {
    this.shouldReconnect = false;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this._stopPing();
    if (this.ws) {
      try {
        this.ws.close();
      } catch (e) {
        // Socket already closed by the server-side reaper.
        console.warn('[Realtime] ws.close() raised — likely already closed', e);
      }
      this.ws = null;
    }
  }

  /** Helper used by Layout to wire up after login/auto-join. */
  start() {
    const syncKey = syncService.getSyncKey();
    if (!syncKey) return;
    // Access token only lives in tokenStore (in-memory). The httpOnly
    // refresh cookie also auto-attaches on the WS handshake (same-origin).
    const token = tokenStore.getAccessToken();
    this.connect(syncKey, token, syncService.deviceId);
  }

  /**
   * Presence — subscribe to live presence updates.
   * subscriber receives the entire { userId: {status, name, role, ...} } map.
   * Returns an unsubscribe function.
   */
  subscribePresence(fn) {
    this.presenceSubscribers.add(fn);
    // Send the current snapshot synchronously so the caller paints immediately
    try { fn(this.presence); } catch (e) {
      console.warn('[Realtime] presence subscriber initial callback failed', e);
    }
    return () => this.presenceSubscribers.delete(fn);
  }

  _handlePresence(msg) {
    this.presence = msg.users || {};
    this.presenceSubscribers.forEach((fn) => {
      try { fn(this.presence); } catch (e) {
        console.warn('[Realtime] presence subscriber callback failed', e);
      }
    });
    // Also dispatch a window event so plain JS pages can listen if they prefer.
    try {
      window.dispatchEvent(
        new CustomEvent('maidly:presence-updated', { detail: this.presence })
      );
    } catch (e) {
      console.warn('[Realtime] presence-updated dispatch failed', e);
    }
  }

  /**
   * Push the user's working status — 'working' | 'on_break' | 'idle' | 'online'.
   * Server broadcasts an updated presence map to the whole workspace.
   */
  setStatus(status) {
    if (!this.isConnected()) return;
    try {
      this.ws.send(JSON.stringify({ type: 'status', status }));
    } catch (e) {
      console.warn('[Realtime] status send failed; socket may be closing', e);
    }
  }
}

const realtimeService = new RealtimeService();
export default realtimeService;
